import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    getCalibrationSchedulesCalendar,
    getJoinedGreenUsers,
    getAllFacilitiesForTickets
} from '../../Redux/actions';
import {
    Grid, IconButton, Tooltip, CircularProgress,
    FormControl, InputLabel, Select, MenuItem, Typography, Box,
    Button
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import dayjs from 'dayjs';
import CalibrationCalendar from './CalibrationCalendar';
import { THEME_COLOR, selectFocusedStyle, inputLabelFocusedStyle, buttonStyles } from '../../theme/formStyles';
import Filter from '../Common/Filter';
import { updateParams } from '../../utils';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

export default function VisitScheduled() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const today = dayjs();

    const [currentMonth, setCurrentMonth] = useState(today.month()); // 0-indexed
    const [currentYear, setCurrentYear] = useState(today.year());
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [facilities, setFacilities] = useState([]);
    const [greenUsers, setGreenUsers] = useState([]);
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [queryParams, setQueryParams] = useSearchParams();
    const [isSearchOrFilterApplied, setIsSearchOrFilterApplied] = useState(false);

    useEffect(() => {
        dispatch(getAllFacilitiesForTickets()).then(resp => {
            if (resp?.data?.success) {
                setFacilities(resp.data.allData || []);
            }
        });
        dispatch(getJoinedGreenUsers()).then(resp => {
            if (resp?.data?.success) {
                const activeUsers = resp.data.allData?.filter(item => item.status !== 'Inactive');
                const users = activeUsers?.map(item => item.name) || [];
                setGreenUsers(users);
            }
        });
    }, []);

    useEffect(() => {
        fetchSchedules();
    }, [currentMonth, currentYear, queryParams]);

    const fetchSchedules = () => {
        setLoading(true);
        const existingParams = Object.fromEntries([...queryParams]);
        const params = {
            month: currentMonth + 1, // API expects 1-indexed
            year: currentYear,
            ...existingParams,
        };

        dispatch(getCalibrationSchedulesCalendar(params)).then(resp => {
            if (resp?.data?.success) {
                setSchedules(resp.data.data || []);
            } else {
                setSchedules([]);
            }
            setLoading(false);
        }).catch(() => {
            setSchedules([]);
            setLoading(false);
        });
    };

    const goToToday = () => {
        setCurrentMonth(today.month());
        setCurrentYear(today.year());
    };

    const goToPrevMonth = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(prev => prev - 1);
        } else {
            setCurrentMonth(prev => prev - 1);
        }
    };

    const goToNextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(prev => prev + 1);
        } else {
            setCurrentMonth(prev => prev + 1);
        }
    };

    const handleMonthChange = (e) => {
        setCurrentMonth(parseInt(e.target.value));
    };

    const handleYearChange = (e) => {
        setCurrentYear(parseInt(e.target.value));
    };

    const updateQueryParams = (params) => {
        const existingParams = Object.fromEntries([...queryParams]);
        const searchParams = updateParams(existingParams, params);
        navigate({ search: searchParams.toString() }, { replace: true });
    };

    const yearOptions = [];
    for (let y = today.year() - 2; y <= today.year() + 5; y++) {
        yearOptions.push(y);
    }

    const existingParams = Object.fromEntries([...queryParams]);
    const hasActiveFilters = existingParams.facility_id || existingParams.assignee_name;

    return (
        <div className='w3-padding-large'>
            <Grid container>
                <Grid item xs={12}>
                    {/* Header */}
                    <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Grid item>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                                Calibration Scheduled
                            </Typography>
                        </Grid>
                        <Grid item>
                            <IconButton onClick={() => setShowFilterDialog(true)} title="Apply Filter">
                                <FilterListIcon />
                            </IconButton>
                        </Grid>
                    </Grid>

                    {/* Filter Chips - Rendered by Filter component */}
                    <Box sx={{ mb: 2 }}>
                        <Filter
                            filterArea="VisitScheduled"
                            showFilterDialog={showFilterDialog}
                            setShowFilterDialog={setShowFilterDialog}
                            updateQueryParams={updateQueryParams}
                            queryParams={queryParams}
                            setIsSearchOrFilterApplied={setIsSearchOrFilterApplied}
                        />
                    </Box>

                    {/* Calendar Navigation Bar */}
                    <Grid
                        container
                        alignItems="center"
                        sx={{
                            mb: 0,
                            py: 1.5,
                            px: 2,
                            backgroundColor: '#fff',
                            borderRadius: '8px 8px 0 0',
                            borderBottom: '1px solid #e0e0e0',
                        }}
                    >
                        <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Calendar view">
                                <IconButton size="small" sx={{ color: THEME_COLOR }}>
                                    <CalendarTodayIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={goToToday}
                                sx={{
                                    borderColor: THEME_COLOR,
                                    color: THEME_COLOR,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    minWidth: 'auto',
                                    px: 2,
                                    '&:hover': {
                                        borderColor: '#006666',
                                        backgroundColor: 'rgba(0,128,128,0.04)',
                                    }
                                }}
                            >
                                Today
                            </Button>
                            <IconButton size="small" onClick={goToPrevMonth} sx={{ color: '#555' }}>
                                <KeyboardArrowUpIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={goToNextMonth} sx={{ color: '#555' }}>
                                <KeyboardArrowDownIcon fontSize="small" />
                            </IconButton>
                        </Grid>
                        <Grid item sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControl size="small" variant="standard" sx={{ minWidth: 120 }}>
                                <Select
                                    value={currentMonth}
                                    onChange={handleMonthChange}
                                    disableUnderline
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '16px',
                                        color: '#333',
                                    }}
                                    MenuProps={{ disableScrollLock: true }}
                                >
                                    {MONTHS.map((m, idx) => (
                                        <MenuItem key={idx} value={idx}>{m}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" variant="standard" sx={{ minWidth: 80 }}>
                                <Select
                                    value={currentYear}
                                    onChange={handleYearChange}
                                    disableUnderline
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '16px',
                                        color: '#333',
                                    }}
                                    MenuProps={{ disableScrollLock: true }}
                                >
                                    {yearOptions.map(y => (
                                        <MenuItem key={y} value={y}>{y}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {/* Calendar Grid */}
                    {loading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '400px',
                            backgroundColor: '#fff',
                            borderRadius: '0 0 8px 8px',
                        }}>
                            <CircularProgress sx={{ color: THEME_COLOR }} />
                        </Box>
                    ) : (
                        <CalibrationCalendar
                            month={currentMonth}
                            year={currentYear}
                            schedules={schedules}
                            facilities={facilities}
                        />
                    )}
                </Grid>
            </Grid>
        </div>
    );
}
